import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Flex,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Select
} from '@chakra-ui/react';
import axios from 'axios';

import { v4 as uuidv4 } from 'uuid';

const countryMap = {
  "United States": "US",
  "Canada": "CA",
  "Mexico": "MX"
};

const usStates = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming"
];

const CreditCardForm = () => {
  const [form, setForm] = useState({
    name: '',
    number: '',
    expMonth: '',
    expYear: '',
    cvc: '',
    address: {
      streetAddress: '',
      city: '',
      region: '',
      postalCode: '',
      country: ''
    },
    additionalAmount: '0'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const formatCardNumber = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 16); // Keep only 16 digits
    return digitsOnly.replace(/(.{4})/g, '$1 ').trim(); // Add space every 4 digits
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`name:${name}, value: ${value}` )
    if (name === 'number') {
      const formatted = formatCardNumber(value);
      setForm((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    // Prevent invalid characters for month and year
    if (name === 'expMonth') {
      let digitsOnly = value.replace(/\D/g, '').slice(0, 2);
      
      // Prevent invalid months like 00 or >12
      if (digitsOnly.length === 1 && digitsOnly > '1') {
        digitsOnly = '0' + digitsOnly;
      }
    
      if (digitsOnly.length === 2) {
        const num = parseInt(digitsOnly, 10);
        if (num < 1 || num > 12) {
          return; // Ignore invalid values
        }
      }
    
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    
    // Prevent invalid years
    if (name === 'expYear') {
      let digitsOnly = value.replace(/\D/g, '').slice(0, 4);
    
      // Only validate if 4 digits entered
      if (digitsOnly.length === 4) {
        const year = parseInt(digitsOnly, 10);
        const currentYear = new Date().getFullYear();
    
        if (year < currentYear) {
          return; // Ignore invalid year
        }
      }
    
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }

    // Apply maximum limit for "additionalAmount"
    // if (name === "additionalAmount") {
    //   const numericValue = Number(value);

    //   // Block values over 1893 or negative numbers
    //   if (numericValue > 1893 || numericValue < 0 || isNaN(numericValue)) return;

    //   // if (!/^\d+$/.test(value)) return; -- isNAN is the output of numericValue when it's not a number 

    //   setForm((prev) => ({
    //     ...prev,
    //     [name]: numericValue
    //   }));
    //   return;
    // }   
    if (name === "additionalAmount") {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
    
      // Convert to number for validation
      const numericValue = digitsOnly ? Number(digitsOnly) : 0;
    
      // Block numbers greater than 1893 or negative
      if (numericValue > 1893) return; // no upper bound
      // Don't block zero or empty string while typing
      // Negative numbers are already removed by \D, so no need to check
    
      // Update state with string to allow deleting all
      setForm((prev) => ({
        ...prev,
        [name]: digitsOnly
      }));
      return;
    }

    if (name === 'cvc') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 3);
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    
    

    // Nested address handling
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    onOpen();

    const selectedCountryName = form.address.country;
    const countryCode = countryMap[selectedCountryName];

    const payload = {
      card: {
        name: form.name,
        number: form.number.replace(/\s+/g, ''), // ← removes all spaces
        expMonth: form.expMonth,
        expYear: form.expYear,
        cvc: form.cvc,
        address: {
          streetAddress: form.address.streetAddress,
          city: form.address.city,
          region: form.address.region,
          postalCode: form.address.postalCode,
          country: countryCode
        }
      }
    };

    try {
      // Workflow 
      // 1. Grab the o-auth token from backend (TODO for production)
      // 2. Tokenize the credit card info using quickbooks payment api
      // 3. Send the token to the backend server to start the payment authorization workflow       
      
      // Step 1:
      const { data } = await axios.get('/api/quickbooks-token');
      const accessToken = data.accessToken;
      const realmId = data.realmId;
      
      // Step 2: 
      const tokenizedCC = await axios.post('https://sandbox.api.intuit.com/quickbooks/v4/payments/tokens', 
        payload, 
        {
          'Content-Type': 'application/json',
          'request-id': uuidv4().toString()
        }
      );
      
      console.log('Payload sent:', payload);
      console.log('Tokenized CC: ', tokenizedCC.data);

      // Step 3: send token and amount to backend to process
      const amount = 38 + Number(form.additionalAmount);
      const response = await axios.post('/api/payment', {
        token: tokenizedCC.data,
        amount: amount
      });

      // ✅ Navigate if successful
      if (response.status === 201 && response.data.message.includes('Successfully')) {
        navigate('/dashboard');
      } else {
        console.error('Payment failed:', response.data);
      }

    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <>
      <Flex
        direction="column"
        align="center"
        justify="center"
        minHeight="100vh"
        bg="#193048"
        px={4}
      >
        <Box
          bg="white"
          p={8}
          borderRadius="lg"
          boxShadow="lg"
          w="100%"
          maxW="500px"
        >
          <VStack spacing={5} as="form" onSubmit={handleSubmit}>
            <Heading size="md" textAlign="center">
              Credit Card Payment
            </Heading>

            <FormControl isRequired>
              <FormLabel>Name on Card</FormLabel>
              <Input name="name" value={form.name} onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Card Number</FormLabel>
              <Input inputMode="numeric" pattern="[0-9 ]*" name="number" value={form.number} onChange={handleChange} />
            </FormControl>

            <Flex gap={4}>
              <FormControl isRequired>
                <FormLabel>Exp Month</FormLabel>
                <Input inputMode="numeric" pattern="[0-9]*" name="expMonth" value={form.expMonth} onChange={handleChange} placeholder="MM" />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Exp Year</FormLabel>
                <Input inputMode="numeric" pattern="[0-9]*" name="expYear" value={form.expYear} onChange={handleChange} placeholder="YYYY" />
              </FormControl>
            </Flex>

            <FormControl isRequired>
              <FormLabel>CVC</FormLabel>
              <Input inputMode="numeric" pattern="[0-9]*" name="cvc" value={form.cvc} onChange={handleChange} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Street Address</FormLabel>
              <Input name="address.streetAddress" value={form.address.streetAddress} onChange={handleChange} />
            </FormControl>

            <Flex gap={4}>
              <FormControl isRequired>
                <FormLabel>City</FormLabel>
                <Input name="address.city" value={form.address.city} onChange={handleChange} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>State/Region</FormLabel>
                <Select name="address.region" value={form.address.region} onChange={handleChange}>
                  {usStates.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </Select>
              </FormControl>
            </Flex>

            <Flex gap={4}>
              <FormControl isRequired>
                <FormLabel>Postal Code</FormLabel>
                <Input name="address.postalCode" value={form.address.postalCode} onChange={handleChange} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Country</FormLabel>
                <Select name="address.country" value={form.address.country} onChange={handleChange}>
                  {Object.keys(countryMap).map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </Select>
              </FormControl>
            </Flex>

            <Text fontSize="m" color="gray.500" textAlign="justify">
              I authorize Balgod to recreive and process my payment information with a minimum of $38.00 for a period of 52 weeks, and $8 starting week 53 to the end of the ocntract on week 104.
            </Text>

            <FormControl>
              <FormLabel>
                I would like to add more than $38 per week. What is the additional amount you'd like to give?
              </FormLabel>

              {/* 💡 Dynamic Total */}
              <Text mb={2} color="gray.600">
                TOTAL = ${38 + (Number(form.additionalAmount) || 0)}
              </Text>

              <Input inputMode="numeric" pattern="[0-9]*" name="additionalAmount" value={form.additionalAmount} onChange={handleChange} type="number" min="0" max="1893" />
            </FormControl>

            <Button
              colorScheme="teal"
              size="lg"
              mt={4}
              type="submit"
              isDisabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Pay Now'}
            </Button>

            <Text fontSize="sm" color="gray.500" textAlign="center">
              Secure and encrypted payment
            </Text>
          </VStack>
        </Box>
      </Flex>

      {/* Loading Modal */}
      <Modal isOpen={isOpen} onClose={() => {}} isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" shadow="none">
          <ModalBody display="flex" justifyContent="center" alignItems="center" p={10}>
            <Spinner size="xl" color="teal.400" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreditCardForm;
